import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from './apiClient'; // centralized axios instance

// Fetch all learning modules
export const fetchLearningModules = createAsyncThunk(
  'learningModules/fetchLearningModules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/learning-modules/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert a new learning module
export const insertLearningModule = createAsyncThunk(
  'learningModules/insertLearningModule',
  async (moduleData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/learning-modules/', moduleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update a learning module
export const updateLearningModule = createAsyncThunk(
  'learningModules/updateLearningModule',
  async (moduleData, { rejectWithValue }) => {
    try {
      const { id, ...data } = moduleData;
      const response = await apiClient.put(`/learning-modules/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a learning module
export const deleteLearningModule = createAsyncThunk(
  'learningModules/deleteLearningModule',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/learning-modules/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update status
export const updateLearningModuleStatus = createAsyncThunk(
  'learningModules/updateLearningModuleStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/learning-modules/${id}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update ICT ID
export const updateLearningModuleICTId = createAsyncThunk(
  'learningModules/updateLearningModuleICTId',
  async ({ id, ictId_id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/learning-modules/${id}/ict-id`, { ictId_id });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const learningModulesSlice = createSlice({
  name: 'learningModules',
  initialState: {
    modules: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchLearningModules.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLearningModules.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = action.payload;
      })
      .addCase(fetchLearningModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Insert
      .addCase(insertLearningModule.fulfilled, (state, action) => {
        state.modules.push(action.payload);
      })

      // Update
      .addCase(updateLearningModule.fulfilled, (state, action) => {
        const index = state.modules.findIndex((module) => module.id === action.payload.id);
        if (index !== -1) {
          state.modules[index] = action.payload;
        }
      })

      // Delete
      .addCase(deleteLearningModule.fulfilled, (state, action) => {
        state.modules = state.modules.filter((module) => module.id !== action.payload);
      })

      // Update status
      .addCase(updateLearningModuleStatus.fulfilled, (state, action) => {
        const index = state.modules.findIndex((module) => module.id === action.payload.id);
        if (index !== -1) {
          state.modules[index] = action.payload;
        }
      })

      // Update ICT ID
      .addCase(updateLearningModuleICTId.fulfilled, (state, action) => {
        const index = state.modules.findIndex((module) => module.id === action.payload.id);
        if (index !== -1) {
          state.modules[index] = action.payload;
        }
      });
  },
});

export default learningModulesSlice.reducer;
